import { useToast } from '@driftt/dds'
import axios from 'axios'
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { ConversationAPI } from 'api/index'
import { PROMPT_MODES } from 'components/AIConfiguration/LeadQualification/constants'
import {
  PersonaPayload,
  PersonaResponse,
  QualificationSettings,
} from 'components/AIConfiguration/LeadQualification/types'

export const ATTRIBUTES_QUERY_KEY = 'qualification'

const DEFAULT_SETTINGS = {
  questionConfig: {
    isAI: true,
  },
  scoringConfig: {
    isAI: true,
  },
}

/* eslint-disable @typescript-eslint/camelcase */
const ERROR_MESSAGES: Record<string, string> = {
  minimum_number_of_criteria_to_meet_exceeds_total:
    'Minimum exceeds number of qualification rules',
  number_of_required_criteria_exceeds_minimum_met:
    'Qualification rules exceed minimum met',
  minimum_number_of_criteria_must_be_greater_than_0:
    'Qualification cannot be deleted as at least one rule is required.',
  default:
    "Oops! Something went wrong. \nOur team has been notified, and we're working to fix it",
}

const getAttributeSettings = async (url: string) => {
  const res = await ConversationAPI.get<PersonaResponse[]>(url)

  return {
    ...DEFAULT_SETTINGS,
    qualificationPersonas: (res?.data || ([] as PersonaResponse[])).map(
      ({ prompt, attributes, ...data }) => ({
        ...data,
        prompt,
        attributes,
        mode:
          !prompt && attributes.length > 0
            ? PROMPT_MODES.MANUAL
            : PROMPT_MODES.AI,
      })
    ),
  }
}

const deleteQualificationPersona = async (qualificationPersonaId: number) =>
  await ConversationAPI.delete(`qualification/${qualificationPersonaId}`)

const updateQualificationPersona = async (payload: PersonaPayload) =>
  !!payload.id
    ? await ConversationAPI.put<PersonaResponse>(
        `qualification/${payload.id}`,
        payload
      )
    : await ConversationAPI.post<PersonaResponse>(`qualification`, payload)

export const useGPTQualificationAttributes = (config?: {
  onSavePersonaSuccess?: () => void
  shouldFetchAttributes?: boolean
}) => {
  const { onSavePersonaSuccess } = config || {}
  const queryClient = useQueryClient()
  const toast = useToast()
  const { isLoading, data: attributeSettings } = useQuery({
    queryKey: [ATTRIBUTES_QUERY_KEY],
    queryFn: ({ queryKey }) => getAttributeSettings(queryKey[0]),
    onError: (err) => {
      if (axios.isAxiosError(err) && err.message.includes('404')) {
        toast({
          message: 'There was an issue fetching qualification attributes',
          variant: 'error',
        })
      }
    },
    refetchOnWindowFocus: false,
    enabled: !!config?.shouldFetchAttributes,
  })

  const handleDeletePersona = useMutation(deleteQualificationPersona, {
    onSuccess: () => {
      queryClient.invalidateQueries(ATTRIBUTES_QUERY_KEY)
      toast({
        message: 'Deleted successfully!',
        variant: 'success',
      })
    },
    onError: () => {
      toast({
        message: 'An error occurred on deletion!',
        variant: 'error',
      })
    },
  })

  const handleSavePersona = useMutation({
    mutationFn: updateQualificationPersona,
    onMutate: async (newAttributes) => {
      await queryClient.cancelQueries({ queryKey: ATTRIBUTES_QUERY_KEY })

      const previousSettings = queryClient.getQueryData(ATTRIBUTES_QUERY_KEY)

      queryClient.setQueryData(ATTRIBUTES_QUERY_KEY, (prev: unknown) => {
        const old = prev as QualificationSettings
        return prev
          ? {
              ...old,
              attributeConfig: {
                id: newAttributes.id,
                prompt: newAttributes.prompt,
                attributes: newAttributes.attributes,
                mode:
                  newAttributes.prompt === null &&
                  newAttributes.attributes.length > 0
                    ? PROMPT_MODES.MANUAL
                    : PROMPT_MODES.AI,
              },
            }
          : old
      })

      return { previousSettings }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(ATTRIBUTES_QUERY_KEY)
      toast({
        message: 'Qualification rules saved successfully',
        variant: 'success',
      })
      if (onSavePersonaSuccess) onSavePersonaSuccess()
    },
    onError: (err, _updatedCriteria, context) => {
      let errorMessage = ERROR_MESSAGES.default

      if (axios.isAxiosError(err) && err.response?.data?.message) {
        const apiMessage = String(err.response.data.message)
        errorMessage = ERROR_MESSAGES[apiMessage] ?? ERROR_MESSAGES.default
      }

      queryClient.setQueryData(ATTRIBUTES_QUERY_KEY, context?.previousSettings)
      toast({
        message: errorMessage,
        variant: 'error',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ATTRIBUTES_QUERY_KEY })
    },
  })

  return useMemo(
    () => ({
      qualificationPersona: attributeSettings?.qualificationPersonas?.[0],
      handleDeletePersona,
      handleSavePersona,
      isLoading,
    }),
    [
      attributeSettings?.qualificationPersonas,
      handleDeletePersona,
      handleSavePersona,
      isLoading,
    ]
  )
}
